import { hasPermission } from 'core/auth/rbac'
import { CattleIcon } from 'core/components/Icons'
import { Badge } from 'core/components/ui/Badge'
import { Drawer } from 'core/components/ui/Drawer'
import { FilterBar } from 'core/components/ui/FilterBar'
import { PageHeader } from 'core/components/ui/PageHeader'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import { useState } from 'react'
import { FarmerSaleRecord } from '../../domain/entities/FarmerSale'
import { FarmerDirectory } from '../components/FarmerDirectory'
import { useFarmerSales } from '../hooks/useFarmerSales'

const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(val)
}

const formatDate = (val: string) => {
  return new Date(val).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const paymentModeTones: Record<string, 'primary' | 'warning' | 'secondary'> = {
  upi: 'primary',
  credit: 'warning',
}

const getPaymentModeTone = (mode: string): 'primary' | 'warning' | 'secondary' => {
  return paymentModeTones[mode] || 'secondary'
}

export function FarmerSalesPage() {
  const { user } = useAuth()
  const canExport = hasPermission(user, 'export_sales_reports')
  const {
    records,
    loading,
    error,
    search,
    setSearch,
    paymentMode,
    setPaymentMode,
    fpoId,
    setFpoId,
    retailerId,
    setRetailerId,
    vlccId,
    setVlccId,
    fpoOptions,
    retailerOptions,
    vlccOptions,
    page,
    setPage,
    totalPages,
    totalItems,
    resetFilters,
  } = useFarmerSales(10)

  const [selectedRecord, setSelectedRecord] = useState<FarmerSaleRecord | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const isFilterActive = !!(
    search ||
    paymentMode !== undefined ||
    fpoId !== undefined ||
    retailerId !== undefined ||
    vlccId !== undefined
  )

  const handleInspectLog = (record: FarmerSaleRecord) => {
    setSelectedRecord(record)
    setIsDrawerOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Farmer Sales Ledger"
        description="Monitor secondary sales of cattle feed bags made by retailers to individual dairy farmers."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Farmer Sales' }]}
        actions={
          canExport ? (
            <div className="flex items-center gap-2">
              <a
                href="/reports/SanjeevaniSetu-Cattle-Feed-Sales-13th July 26.pdf"
                download="SanjeevaniSetu-Cattle-Feed-Sales-13th July 26.pdf"
                className="px-3 py-1.5 text-xs font-semibold bg-bg hover:bg-surface border border-border text-text-primary rounded-btn shadow-sm transition-colors flex items-center gap-1.5"
              >
                <svg className="h-3.5 w-3.5 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Export PDF Report
              </a>
              <a
                href="/reports/Sales report on 13th July 26 - Evening.xlsx"
                download="Sales report on 13th July 26 - Evening.xlsx"
                className="px-3 py-1.5 text-xs font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn shadow transition-colors flex items-center gap-1.5"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export Excel Summary
              </a>
            </div>
          ) : undefined
        }
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search farmer name, phone or ID..."
        searchValue={search}
        onSearchChange={setSearch}
        showReset={isFilterActive}
        onReset={resetFilters}
      >
        {/* FPO Filter */}
        <select
          value={fpoId || ''}
          onChange={(e) => {
            const val = e.target.value
            setFpoId(val === '' ? undefined : val)
            setPage(1)
          }}
          className="w-full sm:w-auto min-w-[130px] h-9 px-3 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold hover:border-primary/50 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-colors cursor-pointer"
        >
          <option value="">All FPOs</option>
          {fpoOptions.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>

        {/* Retailer Filter */}
        <select
          value={retailerId || ''}
          onChange={(e) => {
            const val = e.target.value
            setRetailerId(val === '' ? undefined : val)
            setPage(1)
          }}
          className="w-full sm:w-auto min-w-[130px] h-9 px-3 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold hover:border-primary/50 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-colors cursor-pointer"
        >
          <option value="">All Retailers</option>
          {retailerOptions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        {/* VLCC Filter */}
        <select
          value={vlccId || ''}
          onChange={(e) => {
            const val = e.target.value
            setVlccId(val === '' ? undefined : val)
            setPage(1)
          }}
          className="w-full sm:w-auto min-w-[130px] h-9 px-3 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold hover:border-primary/50 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-colors cursor-pointer"
        >
          <option value="">All VLCCs</option>
          {vlccOptions.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>

        {/* Payment Mode Selector */}
        <select
          value={paymentMode || ''}
          onChange={(e) => {
            const val = e.target.value
            setPaymentMode(val === '' ? undefined : val)
            setPage(1)
          }}
          className="w-full sm:w-auto min-w-[140px] h-9 px-3 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold hover:border-primary/50 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-colors cursor-pointer"
        >
          <option value="">All Payment Modes</option>
          <option value="cash">Cash Mode</option>
          <option value="credit">Credit Mode</option>
          <option value="upi">UPI/QR Mode</option>
        </select>
      </FilterBar>

      {/* Ledger list directory */}
      <FarmerDirectory
        records={records}
        loading={loading}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={10}
        onSelect={handleInspectLog}
      />

      {/* Audit Drawer Sidebar */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Inspection Log: Farmer Sale"
        footer={
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="w-full sm:w-auto px-4 py-2 text-xs font-bold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn shadow transition-colors"
          >
            Close Logs
          </button>
        }
      >
        {selectedRecord && (
          <div className="space-y-6">
            {/* Top summary badge card */}
            <div className="bg-bg/50 border border-border rounded-card p-4 flex flex-col items-center text-center">
              <span className="text-xs text-text-secondary font-medium uppercase tracking-wider">Transaction ID</span>
              <span className="text-base font-bold text-text-primary tracking-tight mt-1">{selectedRecord.id}</span>
              <div className="mt-3">
                <Badge
                  tone={getPaymentModeTone(selectedRecord.paymentMode)}
                  variant="subtle"
                  className="uppercase font-bold"
                >
                  {selectedRecord.paymentMode} Mode
                </Badge>
              </div>
            </div>

            {/* Financial Breakdown Section */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider border-b border-border pb-1.5">
                Financial Breakdown
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Quantity Sold</span>
                  <span className="font-semibold text-text-primary">{selectedRecord.units} Units</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Price per Unit (Farmer Rate)</span>
                  <span className="font-semibold text-text-primary">{formatCurrency(selectedRecord.retailPrice)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary font-semibold">Total Invoice Amount</span>
                  <span className="font-bold text-text-primary">{formatCurrency(selectedRecord.amount)}</span>
                </div>
                <div className="border-t border-dashed border-border my-2" />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary font-semibold">Amount Paid</span>
                  <span className="font-bold text-status-active">{formatCurrency(selectedRecord.amountPaid)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary font-semibold">Outstanding Balance</span>
                  <span
                    className={`font-bold ${
                      selectedRecord.amountOutstanding > 0 ? 'text-danger' : 'text-text-secondary'
                    }`}
                  >
                    {formatCurrency(selectedRecord.amountOutstanding)}
                  </span>
                </div>
              </div>
            </div>

            {/* Supply Chain Hierarchy: Samarth -> FPO -> VLCC -> Farmer */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider border-b border-border pb-1.5">
                Supply Chain Hierarchy
              </h4>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3 border border-border rounded-card bg-bg/40 p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge tone="neutral" variant="subtle" className="uppercase text-[10px] font-bold shrink-0">
                      Samarth
                    </Badge>
                    <p className="font-semibold text-text-primary text-sm truncate">Source Manufacturer</p>
                  </div>
                  <p className="font-bold text-text-primary text-sm shrink-0">{selectedRecord.units} Units</p>
                </div>

                <div className="flex justify-center">
                  <span className="text-[10px] font-bold text-text-secondary bg-bg px-2 py-0.5 rounded-full border border-border">
                    {formatCurrency(selectedRecord.samarthPrice)}/unit
                  </span>
                </div>

                {selectedRecord.fpo && (
                  <div className="flex items-center justify-between gap-3 border border-border rounded-card bg-bg/40 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge tone="info" variant="subtle" className="uppercase text-[10px] font-bold shrink-0">
                        FPO
                      </Badge>
                      <div className="min-w-0">
                        <p className="font-semibold text-text-primary text-sm truncate">{selectedRecord.fpo.name}</p>
                        <p className="text-[11px] text-text-secondary font-mono">{selectedRecord.fpo.id}</p>
                      </div>
                    </div>
                    <p className="font-bold text-text-primary text-sm shrink-0">{selectedRecord.units} Units</p>
                  </div>
                )}

                {selectedRecord.retailer && (
                  <div className="flex items-center justify-between gap-3 border border-border rounded-card bg-bg/40 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge tone="warning" variant="subtle" className="uppercase text-[10px] font-bold shrink-0">
                        Retailer
                      </Badge>
                      <div className="min-w-0">
                        <p className="font-semibold text-text-primary text-sm truncate">
                          {selectedRecord.retailer.name}
                        </p>
                        <p className="text-[11px] text-text-secondary font-mono">
                          {selectedRecord.retailer.id}
                          {selectedRecord.retailer.phone ? ` • ${selectedRecord.retailer.phone}` : ''}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-text-primary text-sm shrink-0">{selectedRecord.units} Units</p>
                  </div>
                )}

                {selectedRecord.vlcc && (
                  <div className="flex items-center justify-between gap-3 border border-border rounded-card bg-bg/40 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge tone="secondary" variant="subtle" className="uppercase text-[10px] font-bold shrink-0">
                        VLCC
                      </Badge>
                      <div className="min-w-0">
                        <p className="font-semibold text-text-primary text-sm truncate">{selectedRecord.vlcc.name}</p>
                        <p className="text-[11px] text-text-secondary font-mono">
                          {selectedRecord.vlcc.id}
                          {selectedRecord.vlcc.phone ? ` • ${selectedRecord.vlcc.phone}` : ''}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-text-primary text-sm shrink-0">{selectedRecord.units} Units</p>
                  </div>
                )}

                {selectedRecord.farmer && (
                  <div className="flex items-center justify-between gap-3 border border-border rounded-card bg-bg/40 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge tone="primary" variant="subtle" className="uppercase text-[10px] font-bold shrink-0">
                        Farmer
                      </Badge>
                      <div className="min-w-0">
                        <p className="font-semibold text-text-primary text-sm truncate">{selectedRecord.farmer.name}</p>
                        <p className="text-[11px] text-text-secondary font-mono">
                          {selectedRecord.farmer.id}
                          {selectedRecord.farmer.phone ? ` • ${selectedRecord.farmer.phone}` : ''}
                        </p>
                        {(selectedRecord.cowCount !== undefined || selectedRecord.buffaloCount !== undefined) && (
                          <div className="flex items-center gap-3 mt-1.5 text-xs font-semibold text-text-secondary">
                            <span className="flex items-center gap-1.5">
                              <CattleIcon size={24} className="text-primary inline shrink-0" />
                              Cows: {selectedRecord.cowCount ?? 0}
                            </span>
                            <span>&bull;</span>
                            <span className="flex items-center gap-1.5">
                              <CattleIcon size={24} className="text-primary inline shrink-0" />
                              Buffaloes: {selectedRecord.buffaloCount ?? 0}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="font-bold text-text-primary text-sm shrink-0">{selectedRecord.units} Units</p>
                  </div>
                )}
              </div>
            </div>

            {/* Audit Logs */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider border-b border-border pb-1.5">
                System Audit Info
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Logged Timestamp</span>
                  <span className="text-xs text-text-primary">{formatDate(selectedRecord.createdAt)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Sync Status</span>
                  <span className="text-xs font-bold text-status-active">SUCCESS</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}
